import React, { useEffect } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import GlobalStyle from './styles/GlobalStyle';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { usePortalT } from './i18n/usePortalT';
import { AuthProvider } from './context/AuthContext';
import { OrganizerAuthProvider } from './context/OrganizerAuthContext';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import SupportPage from './pages/SupportPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import LoginPage from './pages/admin/LoginPage';
import ProtectedRoute from './components/admin/ProtectedRoute';
import CampaignListPage from './pages/admin/CampaignListPage';
import CampaignCreatePage from './pages/admin/CampaignCreatePage';
import CampaignEditPage from './pages/admin/CampaignEditPage';
import CampaignDetailPage from './pages/admin/CampaignDetailPage';
import ReportListPage from './pages/admin/ReportListPage';
import ReportDetailPage from './pages/admin/ReportDetailPage';
import AppealListPage from './pages/admin/AppealListPage';
import RewardStorePage from './pages/admin/RewardStorePage';
import OrganizerLoginPage from './pages/organizer/OrganizerLoginPage';
import OrganizerProtectedRoute from './pages/organizer/OrganizerProtectedRoute';
import ActivityListPage from './pages/organizer/ActivityListPage';
import ActivityEditorPage from './pages/organizer/ActivityEditorPage';
import VoucherAuthPage from './pages/organizer/VoucherAuthPage';
import RouteRewardsPage from './pages/organizer/RouteRewardsPage';
import NotFoundPage from './pages/NotFoundPage';

function DocumentTitle() {
  const { pathname } = useLocation();
  const { t } = useLanguage();
  const { t: pt } = usePortalT();

  useEffect(() => {
    const suffix = ' · omw';
    const titles = {
      '/': t.meta.home,
      '/about': t.meta.about,
      '/support': t.meta.support,
      '/privacy': t.meta.privacy,
      '/terms': t.meta.terms,
      '/admin/login': pt('adminNav.brand') + suffix,
      '/admin': pt('adminNav.brand') + suffix,
    };
    if (pathname.startsWith('/admin/activities')) {
      document.title = pt('adminNav.activities') + suffix;
      return;
    }
    if (pathname.startsWith('/admin/campaigns')) {
      document.title = pt('adminNav.pvp') + suffix;
      return;
    }
    if (pathname.startsWith('/admin/reports') || pathname.startsWith('/admin/appeals')) {
      document.title = pt('adminNav.reports') + suffix;
      return;
    }
    if (pathname.startsWith('/admin/rewards')) {
      document.title = pt('adminNav.rewards') + suffix;
      return;
    }
    document.title = titles[pathname] || t.meta.home;
  }, [pathname, t, pt]);

  return null;
}

function AppRoutes() {
  return (
    <>
      <DocumentTitle />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/admin/login" element={<LoginPage />} />
        <Route path="/admin" element={<ProtectedRoute />}>
          <Route index element={<CampaignListPage />} />
          <Route path="campaigns/new" element={<CampaignCreatePage />} />
          <Route path="campaigns/:id" element={<CampaignDetailPage />} />
          <Route path="campaigns/:id/edit" element={<CampaignEditPage />} />
          <Route path="activities" element={<ActivityListPage basePath="/admin/activities" />} />
          <Route path="activities/new" element={<ActivityListPage basePath="/admin/activities" />} />
          <Route path="activities/:id" element={<ActivityEditorPage basePath="/admin/activities" />} />
          <Route path="reports" element={<ReportListPage />} />
          <Route path="reports/:id" element={<ReportDetailPage />} />
          <Route path="appeals" element={<AppealListPage />} />
          <Route path="rewards" element={<RewardStorePage />} />
        </Route>
        <Route path="/organizer/login" element={<OrganizerLoginPage />} />
        <Route path="/organizer" element={<OrganizerProtectedRoute />}>
          <Route index element={<ActivityListPage />} />
          <Route path="new" element={<ActivityListPage />} />
          <Route path="vouchers" element={<VoucherAuthPage />} />
          <Route path="route-rewards" element={<RouteRewardsPage />} />
          <Route path=":id" element={<ActivityEditorPage />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <OrganizerAuthProvider>
          <BrowserRouter>
            <GlobalStyle />
            <AppRoutes />
          </BrowserRouter>
        </OrganizerAuthProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
